import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Search, Filter, MoreVertical, Users, Mail, Shield, AlertCircle, Loader2, Trash2, X, ChevronDown, UserPlus, ArrowUpDown, ArrowUp, ArrowDown, Monitor, Smartphone, MapPin, Globe, Clock, Target, Eye } from 'lucide-react';
import { useAuth, useUser } from '@clerk/clerk-react';
import {
  roleColors,
  rolePriority,
} from '../config/roles';
import { usePermissions } from '../contexts/PermissionsContext';

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  studio: string | null;
  status: 'active' | 'inactive' | 'pending';
  lastLogin: string | null;
  createdAt: string;
}

interface UserSession {
  id: string;
  status: string; // active, ended, revoked, etc.
  deviceType: string;
  browser: string;
  ipAddress: string;
  city: string;
  country: string;
  isMobile: boolean;
  lastActiveAt: string | null;
  createdAt: string | null;
}

interface UserActivity {
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
    createdAt: string;
    lastSignInAt: string | null;
  };
  sessions: UserSession[];
  sessionCount: number;
  workOutput: {
    leadsCreated: number;
    leadsAssigned: number;
  };
}


const statusColors = {
  active: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
  inactive: 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400',
  pending: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
};


type SortField = 'role' | 'lastLogin' | null;
type SortDirection = 'asc' | 'desc';

export default function UsersPage() {
  const { getToken } = useAuth();
  const { user: currentUser } = useUser();
  const { permissions } = usePermissions();
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [deleteConfirmUser, setDeleteConfirmUser] = useState<User | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
  const [editingRoleUserId, setEditingRoleUserId] = useState<string | null>(null);
  const [updatingRole, setUpdatingRole] = useState(false);
  const [sortField, setSortField] = useState<SortField>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userActivity, setUserActivity] = useState<UserActivity | null>(null);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const roleMenuRef = useRef<HTMLDivElement>(null);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuId(null);
      }
      if (roleMenuRef.current && !roleMenuRef.current.contains(event.target as Node)) {
        setEditingRoleUserId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      const data = await response.json();
      if (data.users) {
        setUsers(data.users);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleDeleteUser = async (user: User) => {
    setDeleting(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/users/${user.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete user');
      }

      // Remove user from local state
      setUsers(prev => prev.filter(u => u.id !== user.id));
      setDeleteConfirmUser(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const handleInviteUser = async () => {
    if (!inviteEmail.trim()) {
      setInviteError('Email is required');
      return;
    }

    setInviting(true);
    setInviteError(null);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/users/invite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: inviteEmail, role: inviteRole }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send invitation');
      }

      setInviteSuccess(data.message || `Invitation sent to ${inviteEmail}`);
      setInviteEmail('');
      setInviteRole(availableRoles[0]?.value || '');
      setTimeout(() => {
        setShowInviteModal(false);
        setInviteSuccess(null);
      }, 2000);
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleUpdateRole = async (userId: string, newRole: string) => {
    setUpdatingRole(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/users/${userId}/role`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ role: newRole }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update role');
      }

      // Update local state
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
      setEditingRoleUserId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
    } finally {
      setUpdatingRole(false);
    }
  };

  // Get dynamic roles from database permissions
  const availableRoles = useMemo(() => {
    if (!permissions) return [];
    return Object.keys(permissions).map(roleValue => ({
      value: roleValue,
      label: roleValue.charAt(0).toUpperCase() + roleValue.slice(1), // Capitalize first letter
    }));
  }, [permissions]);

  // Set default invite role when roles are loaded
  useEffect(() => {
    if (availableRoles.length > 0 && !inviteRole) {
      setInviteRole(availableRoles[0].value);
    }
  }, [availableRoles, inviteRole]);

  const fetchUserActivity = async (user: User) => {
    setSelectedUser(user);
    setUserActivity(null);
    setLoadingActivity(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/users/${user.id}/activity`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user activity');
      }

      const data = await response.json();
      setUserActivity(data);
    } catch (err) {
      console.error('Error fetching user activity:', err);
      // Set empty data on error
      setUserActivity({
        user: {
          id: user.id,
          name: user.name,
          email: user.email,
          role: user.role,
          createdAt: user.createdAt,
          lastSignInAt: user.lastLogin,
        },
        sessions: [],
        sessionCount: 0,
        workOutput: {
          leadsCreated: 0,
          leadsAssigned: 0,
        }
      });
    } finally {
      setLoadingActivity(false);
    }
  };

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatTimeAgo = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction or clear sort
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortField(null);
        setSortDirection('asc');
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-4 h-4 text-stone-400" />;
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="w-4 h-4 text-sage" />
      : <ArrowDown className="w-4 h-4 text-sage" />;
  };

  const filteredUsers = users
    .filter(
      (user) =>
        user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (user.studio && user.studio.toLowerCase().includes(searchQuery.toLowerCase()))
    )
    .sort((a, b) => {
      if (!sortField) return 0;

      if (sortField === 'role') {
        const priorityA = rolePriority[a.role] || 99;
        const priorityB = rolePriority[b.role] || 99;
        return sortDirection === 'asc' ? priorityA - priorityB : priorityB - priorityA;
      }

      if (sortField === 'lastLogin') {
        // Handle null values - null means "Never" logged in
        if (!a.lastLogin && !b.lastLogin) return 0;
        if (!a.lastLogin) return sortDirection === 'asc' ? 1 : -1; // Never at end for asc
        if (!b.lastLogin) return sortDirection === 'asc' ? -1 : 1;

        const dateA = new Date(a.lastLogin).getTime();
        const dateB = new Date(b.lastLogin).getTime();
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      }

      return 0;
    });

  const formatLastLogin = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="space-y-6">
      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <div>
            <p className="font-medium text-red-700 dark:text-red-300">Error Loading Data</p>
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/20 dark:bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-stone-900 rounded-xl p-6 flex items-center gap-3 shadow-xl">
            <Loader2 className="w-6 h-6 text-sage animate-spin" />
            <span className="text-stone-700 dark:text-stone-300">Loading users...</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-stone-800 dark:text-stone-100">Users</h1>
          <p className="text-sm sm:text-base text-stone-500 dark:text-stone-400 mt-1">
            Manage platform users and permissions
          </p>
        </div>
        <button
          onClick={() => {
            setShowInviteModal(true);
            setInviteError(null);
            setInviteSuccess(null);
          }}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-sage text-white rounded-xl font-medium hover:bg-sage/90 transition-colors"
        >
          <UserPlus className="w-5 h-5" />
          <span>Invite User</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-sage/10 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-sage" />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-800 dark:text-stone-100">{users.length}</p>
              <p className="text-sm text-stone-500">Total Users</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-50 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-800 dark:text-stone-100">
                {users.filter(u => u.status === 'active').length}
              </p>
              <p className="text-sm text-stone-500">Active</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-50 dark:bg-amber-900/20 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-800 dark:text-stone-100">
                {users.filter(u => u.status === 'pending').length}
              </p>
              <p className="text-sm text-stone-500">Pending</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-50 dark:bg-red-900/20 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-800 dark:text-stone-100">
                {users.filter(u => u.role === 'admin').length}
              </p>
              <p className="text-sm text-stone-500">Admins</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
          <input
            type="text"
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-xl text-sm text-stone-700 dark:text-stone-300 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-sage/50 focus:border-sage"
          />
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-xl text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors">
          <Filter className="w-5 h-5" />
          Filters
        </button>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-3">
        {filteredUsers.map((user) => (
          <div key={user.id} className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-stone-200 dark:bg-stone-700 rounded-full flex items-center justify-center text-stone-600 dark:text-stone-300 font-medium text-sm">
                  {user.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <p className="font-medium text-stone-700 dark:text-stone-300">{user.name}</p>
                  <p className="text-sm text-stone-500 truncate max-w-[180px]">{user.email}</p>
                </div>
              </div>
              <div className="relative" ref={openMenuId === user.id ? menuRef : null}>
                <button
                  onClick={() => setOpenMenuId(openMenuId === user.id ? null : user.id)}
                  className="p-2 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-400 transition-colors"
                >
                  <MoreVertical className="w-5 h-5" />
                </button>
                {openMenuId === user.id && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-stone-800 rounded-lg shadow-lg border border-stone-200 dark:border-stone-700 py-1 z-10">
                    <button
                      onClick={() => {
                        setOpenMenuId(null);
                        fetchUserActivity(user);
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700"
                    >
                      <Eye className="w-4 h-4" />
                      View Activity
                    </button>
                    <button
                      onClick={() => {
                        setOpenMenuId(null);
                        setDeleteConfirmUser(user);
                      }}
                      disabled={user.id === currentUser?.id || user.role === 'admin'}
                      className={`w-full flex items-center gap-2 px-4 py-2 text-left text-sm ${
                        user.id === currentUser?.id || user.role === 'admin'
                          ? 'text-stone-400 cursor-not-allowed'
                          : 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                      }`}
                    >
                      <Trash2 className="w-4 h-4" />
                      {user.id === currentUser?.id ? 'Cannot delete self' : user.role === 'admin' ? 'Cannot delete admin' : 'Delete User'}
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              <span className={`text-xs font-medium px-2 py-1 rounded-full capitalize ${roleColors[user.role] || 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400'}`}>
                {user.role}
              </span>
              <span className={`text-xs font-medium px-2 py-1 rounded-full capitalize ${statusColors[user.status]}`}>
                {user.status}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm text-stone-500">
              <span>{user.studio || 'No studio'}</span>
              <span>Last: {formatLastLogin(user.lastLogin)}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800">
        <table className="w-full">
          <thead>
            <tr className="border-b border-stone-200 dark:border-stone-800">
              <th className="text-left px-6 py-4 text-sm font-medium text-stone-500 dark:text-stone-400">User</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-stone-500 dark:text-stone-400">
                <button
                  onClick={() => handleSort('role')}
                  className="flex items-center gap-1 hover:text-stone-700 dark:hover:text-stone-200 transition-colors"
                >
                  Role
                  {getSortIcon('role')}
                </button>
              </th>
              <th className="text-left px-6 py-4 text-sm font-medium text-stone-500 dark:text-stone-400">Studio</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-stone-500 dark:text-stone-400">Status</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-stone-500 dark:text-stone-400">
                <button
                  onClick={() => handleSort('lastLogin')}
                  className="flex items-center gap-1 hover:text-stone-700 dark:hover:text-stone-200 transition-colors"
                >
                  Last Active
                  {getSortIcon('lastLogin')}
                </button>
              </th>
              <th className="text-left px-6 py-4 text-sm font-medium text-stone-500 dark:text-stone-400"></th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.id} className="border-b border-stone-100 dark:border-stone-800 last:border-0 hover:bg-stone-50 dark:hover:bg-stone-800/50 transition-colors">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-stone-200 dark:bg-stone-700 rounded-full flex items-center justify-center text-stone-600 dark:text-stone-300 font-medium">
                      {user.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <p className="font-medium text-stone-700 dark:text-stone-300">{user.name}</p>
                      <p className="text-sm text-stone-500">{user.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="relative" ref={editingRoleUserId === user.id ? roleMenuRef : null}>
                    <button
                      onClick={() => setEditingRoleUserId(editingRoleUserId === user.id ? null : user.id)}
                      disabled={updatingRole || user.id === currentUser?.id}
                      className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full capitalize ${roleColors[user.role] || 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400'} ${user.id === currentUser?.id ? 'cursor-not-allowed opacity-70' : 'hover:opacity-80 cursor-pointer'}`}
                    >
                      {updatingRole && editingRoleUserId === user.id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <>
                          {user.role}
                          {user.id !== currentUser?.id && <ChevronDown className="w-3 h-3" />}
                        </>
                      )}
                    </button>
                    {editingRoleUserId === user.id && user.id !== currentUser?.id && (
                      <div className="absolute left-0 mt-2 w-32 bg-white dark:bg-stone-800 rounded-lg shadow-lg border border-stone-200 dark:border-stone-700 py-1 z-50">
                        {availableRoles.map((role) => (
                          <button
                            key={role.value}
                            onClick={() => handleUpdateRole(user.id, role.value)}
                            className={`w-full text-left px-3 py-2 text-sm capitalize hover:bg-stone-50 dark:hover:bg-stone-700 ${user.role === role.value ? 'text-sage font-medium' : 'text-stone-700 dark:text-stone-300'}`}
                          >
                            {role.label}
                            {user.role === role.value && ' ✓'}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 text-stone-700 dark:text-stone-300">
                  {user.studio || <span className="text-stone-400">—</span>}
                </td>
                <td className="px-6 py-4">
                  <span className={`text-xs font-medium px-2 py-1 rounded-full capitalize ${statusColors[user.status]}`}>
                    {user.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-stone-500">
                  {formatLastLogin(user.lastLogin)}
                </td>
                <td className="px-6 py-4">
                  <div className="relative" ref={openMenuId === user.id ? menuRef : null}>
                    <button
                      onClick={() => setOpenMenuId(openMenuId === user.id ? null : user.id)}
                      className="p-2 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-400 transition-colors"
                    >
                      <MoreVertical className="w-5 h-5" />
                    </button>
                    {openMenuId === user.id && (
                      <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-stone-800 rounded-lg shadow-lg border border-stone-200 dark:border-stone-700 py-1 z-10">
                        <button
                          onClick={() => {
                            setOpenMenuId(null);
                            fetchUserActivity(user);
                          }}
                          className="w-full flex items-center gap-2 px-4 py-2 text-left text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-700"
                        >
                          <Eye className="w-4 h-4" />
                          View Activity
                        </button>
                        <button
                          onClick={() => {
                            setOpenMenuId(null);
                            setDeleteConfirmUser(user);
                          }}
                          disabled={user.id === currentUser?.id || user.role === 'admin'}
                          className={`w-full flex items-center gap-2 px-4 py-2 text-left text-sm ${
                            user.id === currentUser?.id || user.role === 'admin'
                              ? 'text-stone-400 cursor-not-allowed'
                              : 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                          }`}
                        >
                          <Trash2 className="w-4 h-4" />
                          {user.id === currentUser?.id ? 'Cannot delete self' : user.role === 'admin' ? 'Cannot delete admin' : 'Delete User'}
                        </button>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-stone-900 rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-stone-800 dark:text-stone-100">
                Delete User
              </h3>
              <button
                onClick={() => setDeleteConfirmUser(null)}
                className="p-1 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-400"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-stone-600 dark:text-stone-400 mb-2">
              Are you sure you want to delete <strong>{deleteConfirmUser.name}</strong>?
            </p>
            <p className="text-sm text-stone-500 dark:text-stone-500 mb-6">
              This will permanently remove the user from Clerk and soft-delete all associated data
              (studio, therapists, clients, bookings, services).
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirmUser(null)}
                disabled={deleting}
                className="px-4 py-2 rounded-lg border border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteUser(deleteConfirmUser)}
                disabled={deleting}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete User
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-stone-900 rounded-2xl p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-stone-800 dark:text-stone-100">
                Invite User
              </h3>
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteEmail('');
                  setInviteRole(availableRoles[0]?.value || '');
                  setInviteError(null);
                }}
                className="p-1 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-400"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {inviteSuccess ? (
              <div className="text-center py-4">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Mail className="w-6 h-6 text-green-600" />
                </div>
                <p className="text-green-600 dark:text-green-400 font-medium">{inviteSuccess}</p>
              </div>
            ) : (
              <>
                <p className="text-stone-600 dark:text-stone-400 mb-4">
                  Send an invitation email to a new user. They'll be able to create an account and access the platform.
                </p>

                {inviteError && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <p className="text-sm text-red-600 dark:text-red-400">{inviteError}</p>
                  </div>
                )}

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="user@example.com"
                      className="w-full px-3 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-700 dark:text-stone-300 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-sage/50 focus:border-sage"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">
                      Role
                    </label>
                    <select
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value)}
                      className="w-full px-3 py-2 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-700 dark:text-stone-300 focus:outline-none focus:ring-2 focus:ring-sage/50 focus:border-sage"
                    >
                      {availableRoles.map(role => (
                        <option key={role.value} value={role.value}>
                          {role.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex gap-3 justify-end mt-6">
                  <button
                    onClick={() => {
                      setShowInviteModal(false);
                      setInviteEmail('');
                      setInviteRole(availableRoles[0]?.value || '');
                      setInviteError(null);
                    }}
                    disabled={inviting}
                    className="px-4 py-2 rounded-lg border border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleInviteUser}
                    disabled={inviting || !inviteEmail.trim()}
                    className="px-4 py-2 rounded-lg bg-sage text-white hover:bg-sage/90 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {inviting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4" />
                        Send Invitation
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* User Activity Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-stone-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-stone-200 dark:border-stone-800">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-stone-200 dark:bg-stone-700 rounded-full flex items-center justify-center text-stone-600 dark:text-stone-300 font-semibold text-lg">
                  {selectedUser.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-stone-800 dark:text-stone-100">
                    {selectedUser.name}
                  </h3>
                  <p className="text-sm text-stone-500">{selectedUser.email}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setSelectedUser(null);
                  setUserActivity(null);
                }}
                className="p-2 rounded-lg hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-400 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              {loadingActivity ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-sage animate-spin" />
                </div>
              ) : userActivity ? (
                <div className="space-y-6">
                  {/* User Info */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="bg-stone-50 dark:bg-stone-800/50 rounded-xl p-4">
                      <p className="text-xs text-stone-500 mb-1">Role</p>
                      <p className={`text-sm font-medium px-2 py-1 rounded-full capitalize inline-block ${roleColors[selectedUser.role] || 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400'}`}>
                        {selectedUser.role}
                      </p>
                    </div>
                    <div className="bg-stone-50 dark:bg-stone-800/50 rounded-xl p-4">
                      <p className="text-xs text-stone-500 mb-1">Last Sign In</p>
                      <p className="text-sm font-medium text-stone-700 dark:text-stone-300">
                        {formatTimeAgo(userActivity.user.lastSignInAt)}
                      </p>
                    </div>
                    <div className="bg-stone-50 dark:bg-stone-800/50 rounded-xl p-4">
                      <p className="text-xs text-stone-500 mb-1">Active Sessions</p>
                      <p className="text-sm font-medium text-stone-700 dark:text-stone-300">
                        {userActivity.sessionCount}
                      </p>
                    </div>
                    <div className="bg-stone-50 dark:bg-stone-800/50 rounded-xl p-4">
                      <p className="text-xs text-stone-500 mb-1">Account Created</p>
                      <p className="text-sm font-medium text-stone-700 dark:text-stone-300">
                        {new Date(userActivity.user.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  {/* Work Output */}
                  <div>
                    <h4 className="text-sm font-semibold text-stone-700 dark:text-stone-300 mb-3 flex items-center gap-2">
                      <Target className="w-4 h-4" />
                      Work Output
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-sage/10 rounded-xl p-4">
                        <p className="text-2xl font-bold text-sage">{userActivity.workOutput.leadsCreated}</p>
                        <p className="text-sm text-stone-500">Leads Created</p>
                      </div>
                      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
                        <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{userActivity.workOutput.leadsAssigned}</p>
                        <p className="text-sm text-stone-500">Leads Assigned</p>
                      </div>
                    </div>
                  </div>

                  {/* Recent Sessions */}
                  <div>
                    <h4 className="text-sm font-semibold text-stone-700 dark:text-stone-300 mb-3 flex items-center gap-2">
                      <Monitor className="w-4 h-4" />
                      Recent Sessions ({userActivity.sessions.length})
                    </h4>
                    {userActivity.sessions.length === 0 ? (
                      <p className="text-sm text-stone-500 dark:text-stone-400 py-4 text-center bg-stone-50 dark:bg-stone-800/50 rounded-xl">
                        No session history available
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {userActivity.sessions.map((session) => (
                          <div
                            key={session.id}
                            className="bg-stone-50 dark:bg-stone-800/50 rounded-xl p-4"
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-3">
                                {session.isMobile ? (
                                  <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                                    <Smartphone className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                                  </div>
                                ) : (
                                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                                    <Monitor className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                  </div>
                                )}
                                <div>
                                  <p className="font-medium text-stone-700 dark:text-stone-300">
                                    {session.deviceType || 'Unknown Device'}
                                  </p>
                                  <p className="text-sm text-stone-500">{session.browser || 'Unknown Browser'}</p>
                                </div>
                              </div>
                              <span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${
                                session.status === 'active'
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                                  : session.status === 'ended'
                                  ? 'bg-stone-100 dark:bg-stone-700 text-stone-500 dark:text-stone-400'
                                  : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                              }`}>
                                {session.status || 'unknown'}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div className="flex items-center gap-2 text-stone-500">
                                <MapPin className="w-4 h-4" />
                                <span>{session.city || 'Unknown'}, {session.country || '??'}</span>
                              </div>
                              <div className="flex items-center gap-2 text-stone-500">
                                <Globe className="w-4 h-4" />
                                <span>{session.ipAddress || 'Unknown IP'}</span>
                              </div>
                              <div className="flex items-center gap-2 text-stone-500">
                                <Clock className="w-4 h-4" />
                                <span>Last active: {formatTimeAgo(session.lastActiveAt)}</span>
                              </div>
                              <div className="flex items-center gap-2 text-stone-500">
                                <Clock className="w-4 h-4" />
                                <span>Started: {formatDateTime(session.createdAt)}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-stone-500 text-center py-8">Failed to load activity data</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
